import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { Box, Button, CircularProgress } from "@mui/material";
import { keyframes } from "@mui/system";
import React from "react";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import { getHomePremium } from "../styles/homePremiumStyles";

/** Soft metallic band that sweeps across the capsule. */
const goldShineSweep = keyframes`
  0%, 62% { transform: translateX(-140%) skewX(-18deg); opacity: 0; }
  68% { opacity: 0.85; }
  82% { transform: translateX(220%) skewX(-18deg); opacity: 0; }
  100% { transform: translateX(220%) skewX(-18deg); opacity: 0; }
`;

/** Gentle breathing glow — premium, not flashy. */
const goldBreathPulse = keyframes`
  0%, 100% {
    box-shadow:
      0 10px 28px rgba(201, 146, 26, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.65),
      inset 0 -2px 4px rgba(160, 110, 20, 0.24);
  }
  50% {
    box-shadow:
      0 12px 34px rgba(201, 146, 26, 0.42),
      inset 0 1px 0 rgba(255, 255, 255, 0.78),
      inset 0 -2px 4px rgba(160, 110, 20, 0.2);
  }
`;

/** Wide brushed-gold CTA for the home / library cards (React + MUI `sx`). */
export default function HomePlayButton({
    onClick,
    disabled = false,
    loading = false,
    ariaLabel = "Solve Puzzle",
    label = "Solve Puzzle",
}) {
    const { isDark } = useSlidingAppearance();
    const home = getHomePremium(isDark);
    const isIdle = !disabled && !loading;

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                pt: 0.75,
                pb: 0.25,
            }}
        >
            <Button
                type="button"
                onClick={onClick}
                disabled={disabled || loading}
                aria-label={ariaLabel}
                startIcon={
                    loading ? null : (
                        <PlayArrowRoundedIcon sx={{ fontSize: 22, color: home.goldText }} />
                    )
                }
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    isolation: "isolate",
                    width: "100%",
                    minHeight: 52,
                    borderRadius: "999px",
                    px: 2.5,
                    py: 1.25,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 800,
                    fontSize: "0.9375rem",
                    fontFamily:
                        '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
                    color: home.goldText,
                    background: home.goldMetal,
                    border: "1px solid rgba(255, 240, 180, 0.9)",
                    boxShadow: isDark
                        ? "0 10px 28px rgba(201, 146, 26, 0.28), inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -2px 4px rgba(160, 110, 20, 0.3)"
                        : "0 10px 28px rgba(201, 146, 26, 0.32), inset 0 1px 0 rgba(255,255,255,0.75), inset 0 -2px 4px rgba(160, 110, 20, 0.22)",
                    WebkitTapHighlightColor: "transparent",
                    transition: "transform 0.14s ease, filter 0.18s ease, box-shadow 0.18s ease",
                    willChange: isIdle ? "transform" : "auto",
                    ...(isIdle
                        ? {
                              animation: `${goldBreathPulse} 3.6s ease-in-out infinite`,
                          }
                        : null),
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "38%",
                        height: "100%",
                        pointerEvents: "none",
                        zIndex: 1,
                        background:
                            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 48%, transparent 100%)",
                        opacity: 0,
                        ...(isIdle
                            ? {
                                  animation: `${goldShineSweep} 4.2s ease-in-out infinite`,
                              }
                            : null),
                    },
                    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                        position: "relative",
                        zIndex: 2,
                    },
                    "& .MuiButton-startIcon": {
                        mr: 0.75,
                    },
                    "&:hover": {
                        background: home.goldMetal,
                        filter: "brightness(1.04)",
                    },
                    "&:active:not(.Mui-disabled)": {
                        transform: "scale(0.96)",
                        filter: "brightness(0.98)",
                        animation: "none",
                        "&::after": {
                            animation: "none",
                            opacity: 0,
                        },
                    },
                    "&.Mui-disabled": {
                        color: "rgba(26, 18, 5, 0.5)",
                        background: "linear-gradient(185deg, #e8d89a 0%, #c4a84a 100%)",
                        boxShadow: "none",
                        animation: "none",
                        "&::after": {
                            animation: "none",
                            opacity: 0,
                        },
                    },
                    "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                        willChange: "auto",
                        "&::after": {
                            animation: "none",
                            opacity: 0,
                        },
                        "&:active:not(.Mui-disabled)": {
                            transform: "scale(0.96)",
                        },
                    },
                }}
            >
                {loading ? (
                    <CircularProgress size={22} sx={{ color: home.goldText, position: "relative", zIndex: 2 }} />
                ) : (
                    <Box component="span" sx={{ position: "relative", zIndex: 2 }}>
                        {label}
                    </Box>
                )}
            </Button>
        </Box>
    );
}
